import { Form, Formik, FormikErrors } from 'formik';
import { useState } from 'react';

import { ResourceType, WebhookResourceTypes } from '../../../../../types/WebhookMessages';
import { Button } from '../../../../cdl/Button/Button';
import { ReadOnlyField } from '../../../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../../../cdl/Text/Text';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../../../common/buttons/ButtonGroup';
import { translate } from '../../../../common/helpers/translate.helper';
import { useCopyToClipboard } from '../../../../common/hooks/useCopyToClipboard';
import { IconCopy } from '../../../../common/icons/cdl/Copy';
import { Box } from '../../../../common/ui/Box';
import { useWebhookCreate } from '../hooks/useWebhookCreate';

import { CreateWebhookContext } from './CreateWebhookContext';
import { FormikWebhookFormSteps } from './FormikWebhookFormSteps';

export const CreateWebhookFormSteps = {
    SELECT_EVENTS: 'SELECT_EVENTS',
    DECIDE_TRIGGERS: 'DECIDE_TRIGGERS',
    CONFIGURE_DESTINATION: 'CONFIGURE_DESTINATION',
} as const;

export type CreateWebhookFormStepsType = (typeof CreateWebhookFormSteps)[keyof typeof CreateWebhookFormSteps];

export interface CreateWebhookFormState {
    name: string;
    url: string;
    customerIds: string[];
    allEventsSelected: boolean;
    resourceTypes: {
        draftEnquiry: boolean;
        enquiry: boolean;
        offer: boolean;
        approvalRequest: boolean;
    };
}

interface CreateWebhookFormErrors {
    name?: string;
    url?: string;
    customerIds?: string;
    resourceTypes?: string;
}

interface AddWebhookDestinationModalContentProps {
    groupId: string;
    onDismiss: () => void;
}

export const CreateWebhookModalContent = ({ groupId, onDismiss }: AddWebhookDestinationModalContentProps) => {
    const webhookCreationMutation = useWebhookCreate();
    const copyToClipboard = useCopyToClipboard();
    const { addErrorToast } = useToasts();
    const [activeFormStep, setActiveFormStep] = useState<CreateWebhookFormStepsType>(
        CreateWebhookFormSteps.SELECT_EVENTS
    );

    const initialValues: CreateWebhookFormState = {
        name: '',
        url: '',
        customerIds: [],
        allEventsSelected: false,
        resourceTypes: {
            draftEnquiry: false,
            enquiry: false,
            offer: false,
            approvalRequest: false,
        },
    };

    const validate = (values: CreateWebhookFormState) => {
        const errors: FormikErrors<CreateWebhookFormErrors> = {};

        if (!values.name) {
            errors.name = translate('webhooks.validation.destinationNameRequired');
        }

        const urlRegex = new RegExp(/^https:\/\//);

        if (!values.url) {
            errors.url = translate('webhooks.validation.endpointUrlRequired');
        } else if (values.url && !urlRegex.test(values.url)) {
            errors.url = translate('webhooks.validation.endpointUrlInvalid');
        }

        return errors;
    };

    const handleSubmit = (values: CreateWebhookFormState) => {
        const resourceTypes = [
            values.resourceTypes.draftEnquiry ? WebhookResourceTypes.DRAFT_ENQUIRY : undefined,
            values.resourceTypes.enquiry ? WebhookResourceTypes.ENQUIRY : undefined,
            values.resourceTypes.offer ? WebhookResourceTypes.OFFER : undefined,
            values.resourceTypes.approvalRequest ? WebhookResourceTypes.APPROVAL_REQUEST : undefined,
        ].filter(Boolean) as ResourceType[];

        const webhookRequest = {
            name: values.name,
            url: values.url,
            customerIds: values.customerIds,
            groupId,
            resourceTypes,
            enabled: true,
        };

        webhookCreationMutation.mutate(webhookRequest, {
            onError: () => {
                addErrorToast(translate('webhooks.creationError'));
            },
        });
    };

    if (webhookCreationMutation.isSuccess) {
        return (
            <Box padding={24} display="grid" gap={16}>
                <Box display="grid" gap={4}>
                    <Text variant="title">{translate('webhooks.signingSecret.title')}</Text>
                    <Text variant="small">{translate('webhooks.signingSecret.description')}</Text>
                </Box>

                <ReadOnlyField
                    value={webhookCreationMutation.data.data.secret}
                    trailingVisual={
                        <Button
                            size="small"
                            emphasis="high"
                            onClick={() => copyToClipboard(webhookCreationMutation.data.data.secret)}
                            LeadingIcon={IconCopy}
                        >
                            {translate('btn.copy')}
                        </Button>
                    }
                />

                <ButtonGroup justifyContent="end">
                    <Button onClick={onDismiss}>{translate('btn.done')}</Button>
                </ButtonGroup>
            </Box>
        );
    }

    return (
        <Box padding={24}>
            <Formik
                initialValues={initialValues}
                validateOnMount={false}
                validateOnBlur={false}
                validate={validate}
                onSubmit={handleSubmit}
            >
                <Form>
                    <CreateWebhookContext.Provider value={{ activeFormStep, setActiveFormStep, onDismiss }}>
                        <FormikWebhookFormSteps />
                    </CreateWebhookContext.Provider>
                </Form>
            </Formik>
        </Box>
    );
};
